import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle, ChartCard } from "./_shell";

/**
 * TabOverviewRadar — 7-axis fit radar for the anchor uni PLUS a peer
 * overlay drop-down that lets applicants compare silhouettes without
 * leaving the page.
 *
 * Anchor silhouette:  filled purple polygon (fill+stroke).
 * Peer silhouette:    dashed navy outline, no fill.
 *
 * The peer list is populated from `/api/universities/{id}/peers` on
 * mount. Users can toggle peers on/off via chips; the first peer is
 * on by default so the comparison is visible without interaction.
 */

const AXES = [
  { key: "prestige",      label: "Prestige" },
  { key: "career",        label: "Career" },
  { key: "campus",        label: "Campus" },
  { key: "city",          label: "City" },
  { key: "diversity",     label: "Diversity" },
  { key: "sport",         label: "Sport" },
  { key: "affordability", label: "Affordability" },
];

const PEER_COLOURS = ["#0F766E", "#B45309", "#9F1239"]; // teal · amber · rose

export default function TabOverviewRadar({ uni }) {
  const fit = uni.fit_scores || {};
  const hasAny = AXES.some((a) => fit[a.key] != null);
  const [peers, setPeers] = useState([]);
  const [enabled, setEnabled] = useState({}); // id → bool

  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uni.id}/peers?limit=3`)
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.peers || [];
        setPeers(list);
        // Turn the first peer on by default so a comparison is visible
        // without any interaction.
        if (list[0]) setEnabled({ [list[0].id]: true });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uni.id]);

  if (!hasAny) return null;

  const size = 400;
  const cx = size / 2, cy = size / 2;
  const r = 92;
  const angle = (i) => (i * 2 * Math.PI) / AXES.length - Math.PI / 2;
  const point = (i, v) => {
    const rr = (r * (v || 0)) / 10;
    return [cx + rr * Math.cos(angle(i)), cy + rr * Math.sin(angle(i))];
  };
  const gridPoints = (v) => AXES.map((_, i) => point(i, v).join(",")).join(" ");
  const silhouette = (scores) =>
    AXES.map((a, i) => point(i, scores[a.key]).join(",")).join(" ");
  const strongest = [...AXES].sort((a, b) => (fit[b.key] || 0) - (fit[a.key] || 0))[0];
  const activePeers = peers.filter((p) => enabled[p.id]);

  return (
    <section className="mb-8" data-testid="tab-overview-radar">
      <div>
        <TabKicker>01 · Overall Fit</TabKicker>
        <TabSectionTitle>Seven dimensions, one silhouette</TabSectionTitle>
      </div>
      <ChartCard
        title="Editorial fit profile"
        legend={<span>Scale 0–10 · further from centre = stronger</span>}
        headerRight={
          strongest && (
            <span className="font-mono text-xs uppercase tracking-widest text-[#7C3AED]">
              Strongest · {strongest.label}
            </span>
          )
        }
      >
        <div className="flex justify-center">
          <svg width="100%" height={size} viewBox={`0 0 ${size} ${size}`}
               preserveAspectRatio="xMidYMid meet"
               className="max-w-full h-auto"
               data-testid="overview-radar-svg">
            {[2, 4, 6, 8, 10].map((v) => (
              <polygon key={v} points={gridPoints(v)}
                       fill="none"
                       className="stroke-[#002147]/10"
                       strokeWidth="1"
                       strokeDasharray={v === 10 ? "0" : "3 2"} />
            ))}
            {AXES.map((_, i) => {
              const [x, y] = point(i, 10);
              return <line key={i} x1={cx} y1={cy} x2={x} y2={y}
                           className="stroke-[#002147]/10" strokeWidth="1" />;
            })}
            {/* Peer silhouettes (rendered first so anchor sits on top). */}
            {activePeers.map((p, idx) => {
              const colour = PEER_COLOURS[peers.findIndex((x) => x.id === p.id) % PEER_COLOURS.length];
              return (
                <polygon key={p.id}
                         points={silhouette(p.fit_scores || {})}
                         fill="none"
                         stroke={colour}
                         strokeWidth="1.5"
                         strokeDasharray="4 3"
                         data-testid={`overview-peer-poly-${p.id}`} />
              );
            })}
            {/* Anchor silhouette. */}
            <polygon points={silhouette(fit)}
                     className="fill-[#7C3AED]/15 stroke-[#7C3AED]"
                     strokeWidth="2.5"
                     data-testid="overview-anchor-poly" />
            {AXES.map((a, i) => {
              const v = fit[a.key] || 0;
              const [x, y] = point(i, v);
              const [tx, ty] = [
                cx + (r + 34) * Math.cos(angle(i)),
                cy + (r + 34) * Math.sin(angle(i)),
              ];
              return (
                <g key={a.key}>
                  <circle cx={x} cy={y} r={3.5} className="fill-[#7C3AED]" />
                  <text x={tx} y={ty} textAnchor="middle"
                        className="font-mono fill-[#002147]/60"
                        style={{ fontSize: 10, letterSpacing: 1 }}>
                    {a.label.toUpperCase()}
                  </text>
                  <text x={tx} y={ty + 14} textAnchor="middle"
                        className="font-mono fill-[#002147]"
                        style={{ fontSize: 13, fontWeight: 700 }}>
                    {v}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend + peer toggle chips. */}
        {peers.length > 0 && (
          <div className="mt-4 border-t border-[#002147]/10 pt-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 mb-3">
              Compare with a peer
            </div>
            <div className="flex flex-wrap gap-2" data-testid="overview-peer-toggles">
              {peers.map((p, i) => {
                const colour = PEER_COLOURS[i % PEER_COLOURS.length];
                const on = !!enabled[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setEnabled((e) => ({ ...e, [p.id]: !e[p.id] }))}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 border font-sans text-sm rounded-full transition
                      ${on
                        ? "bg-white shadow-sm border-transparent"
                        : "bg-transparent border-[#002147]/15 hover:border-[#002147]/40"}`}
                    style={on ? { borderColor: colour, color: colour } : {}}
                    data-testid={`overview-peer-toggle-${p.id}`}
                  >
                    <span
                      className="inline-block h-0.5 w-4"
                      style={{ backgroundColor: colour }}
                    />
                    <span className="text-[#002147]">{p.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[#002147]/40">
                      #{p.rank}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <p className="mt-4 font-sans text-sm text-[#002147]/70 leading-relaxed border-t border-[#002147]/10 pt-4">
          Editorial team scores the university on seven dimensions. The shape gives you
          the university's silhouette at a glance — sharp peaks reveal specialisms,
          flatter profiles indicate well-rounded institutions. Toggle a peer to see
          where the two universities diverge.
        </p>
      </ChartCard>
    </section>
  );
}
